import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    organizationSendingDomainContract,
    sendingDomainContract,
} from "@common/contracts/sending-domain.contract";
import type {
    CreateSendingDomainRequest,
    SendingDomainResponse,
} from "@common/schemas/sending-domain.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import {
    getSendingOwnerQueryKey,
    requireOwner,
    type SendingOwner,
} from "./sendingOwner";
import { sendingEmailQueryKeys } from "./sendingEmail.api";

const workspaceClient = getBackendAPIClient(sendingDomainContract);
const organizationClient = getBackendAPIClient(
    organizationSendingDomainContract,
);

const sendingDomainQueryKeys = {
    all: () => ["sending-domains"] as const,
    owner: (owner: SendingOwner) =>
        [
            ...sendingDomainQueryKeys.all(),
            ...getSendingOwnerQueryKey(owner),
        ] as const,
};

function listDomains(owner: SendingOwner) {
    return "workspaceId" in owner
        ? workspaceClient.listDomains({
              params: { workspaceId: owner.workspaceId },
          })
        : organizationClient.listDomains({
              params: { organizationId: owner.organizationId },
          });
}

function createDomain(owner: SendingOwner, body: CreateSendingDomainRequest) {
    return "workspaceId" in owner
        ? workspaceClient.createDomain({
              params: { workspaceId: owner.workspaceId },
              body,
          })
        : organizationClient.createDomain({
              params: { organizationId: owner.organizationId },
              body,
          });
}

function verifyDomain(owner: SendingOwner, domainId: string) {
    return "workspaceId" in owner
        ? workspaceClient.verifyDomain({
              params: { workspaceId: owner.workspaceId, domainId },
          })
        : organizationClient.verifyDomain({
              params: { organizationId: owner.organizationId, domainId },
          });
}

function deleteDomain(owner: SendingOwner, domainId: string) {
    return "workspaceId" in owner
        ? workspaceClient.deleteDomain({
              params: { workspaceId: owner.workspaceId, domainId },
          })
        : organizationClient.deleteDomain({
              params: { organizationId: owner.organizationId, domainId },
          });
}

export function useSendingDomains(
    owner: SendingOwner | null,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: owner
            ? sendingDomainQueryKeys.owner(owner)
            : sendingDomainQueryKeys.all(),
        queryFn: async (): Promise<SendingDomainResponse[]> => {
            const result = await listDomains(requireOwner(owner));
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load sending domains (status ${result.status})`,
                );
            }
            return result.body.domains;
        },
        enabled: owner !== null && (options?.enabled ?? true),
    });
}

export function useCreateSendingDomain(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: CreateSendingDomainRequest,
        ): Promise<SendingDomainResponse> => {
            const result = await createDomain(requireOwner(owner), body);
            if (result.status !== 201) {
                throw new Error(
                    `Failed to create sending domain (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.owner(owner),
                });
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.all(),
                });
            }
        },
    });
}

export function useVerifySendingDomain(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            domainId: string,
        ): Promise<SendingDomainResponse> => {
            const result = await verifyDomain(requireOwner(owner), domainId);
            if (result.status !== 200) {
                throw new Error(
                    `Failed to verify sending domain (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.owner(owner),
                });
            }
        },
    });
}

export function useDeleteSendingDomain(owner: SendingOwner | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (domainId: string) => {
            await deleteDomain(requireOwner(owner), domainId);
        },
        onSuccess: () => {
            if (owner) {
                queryClient.invalidateQueries({
                    queryKey: sendingDomainQueryKeys.owner(owner),
                });
                queryClient.invalidateQueries({
                    queryKey: sendingEmailQueryKeys.all(),
                });
            }
        },
    });
}
